import { createVoiceTiming } from '@/lib/voice-timing';
import * as db from '@/lib/db';
import {
  captureQueueListFence,
  useAudioQueueStore,
  type QueueFence,
} from '@/lib/audio-queue-store';
import { enhancedVoiceAllowed } from '@/lib/core-cutover';
import { getContentOwnerBinding } from '@/lib/content-owner';
import { getSupabase } from '@/lib/supabase';
import { parseScriptureAnchor } from '@/lib/scripture-anchor';
import { normalizeAnchor } from '@/lib/content-lifecycle/contracts';
import { getAuthEpoch, getAuthUserId } from '@/lib/keepsake/epochs';

const REGISTRY_URL = '/api/audio/registry';
const DEVICE_KEY = 'adia-audio-device-v1';
const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SHA256 = /^[0-9a-f]{64}$/;
let deviceIdForPage: string | null = null;

type RegistryAction =
  | 'register'
  | 'uploaded'
  | 'discover'
  | 'claim'
  | 'bind'
  | 'reconcile'
  | 'retry'
  | 'park'
  | 'discard';

interface RegistryResponse {
  ok?: boolean;
  code?: string;
  queueId?: string;
  registryRevision?: string;
  registryState?: string;
  destinationState?: string;
  storagePath?: string | null;
  uploadToken?: string | null;
  uploadRequired?: boolean;
  transcript?: string | null;
  recoveryLeaseToken?: string | null;
  recoveryLeaseExpiresAt?: string | null;
  deleted?: boolean;
  receiptId?: string;
  destinationEntityType?: 'prayer' | 'bookmark' | null;
  destinationEntityId?: string | null;
  destinationSubId?: string | null;
  destinationOpId?: string | null;
  rows?: RemoteRegistryRow[];
  current?: RemoteRegistryRow;
}

interface RemoteRegistryRow {
  queueId: string;
  captureMode: 'enhanced' | 'browser';
  destinationKind: 'new-prayer' | 'progress-update' | 'testimony' | 'bookmark';
  destinationId: string;
  scriptureAnchor: Record<string, unknown> | null;
  sourceDeviceId: string;
  storagePath: string | null;
  byteSize: number;
  byteSha256: string | null;
  mimeType: string;
  language: string;
  capturedAt: string;
  transcript: string | null;
  attemptCount: number;
  processingState: string;
  registryState: string;
  destinationState: string;
  registryRevision: string;
  recoveryLeaseToken: string | null;
  recoveryLeaseExpiresAt: string | null;
  destinationEntityType?: 'prayer' | 'bookmark' | null;
  destinationEntityId?: string | null;
  destinationSubId?: string | null;
  destinationOpId?: string | null;
}

const REGISTRY_STATES = new Set([
  'registered',
  'uploaded',
  'transcribing',
  'transcribed',
  'retry',
  'failed',
  'parked',
  'quarantined',
]);
const DESTINATION_STATES = new Set([
  'unresolved',
  'bound',
  'destination_committed',
  'discarded',
  'account_erasure',
]);
const REGISTRY_REVISION = /^[1-9][0-9]*$/;

export class AudioRegistryProtocolError extends Error {
  readonly code: string;
  readonly current?: RemoteRegistryRow;

  constructor(code: string, current?: RemoteRegistryRow) {
    super(`audio-registry-${code}`);
    this.name = 'AudioRegistryProtocolError';
    this.code = code;
    this.current = current;
  }
}

function compareRevision(left: string, right: string): number {
  if (!REGISTRY_REVISION.test(left) || !REGISTRY_REVISION.test(right)) {
    throw new Error('audio-registry-revision-invalid');
  }
  const a = BigInt(left);
  const b = BigInt(right);
  return a === b ? 0 : a > b ? 1 : -1;
}

function randomUuid(): string {
  return crypto.randomUUID();
}

/** Stable per-browser identity used only to fence cross-device recovery. */
export function getAudioDeviceId(): string {
  if (deviceIdForPage) return deviceIdForPage;
  try {
    const existing = localStorage.getItem(DEVICE_KEY);
    if (existing && UUID_V4.test(existing)) {
      deviceIdForPage = existing;
      return existing;
    }
    const value = randomUuid();
    localStorage.setItem(DEVICE_KEY, value);
    deviceIdForPage = value;
    return value;
  } catch {
    // Private-mode storage failure still gets a per-page fencing identity.
    deviceIdForPage = randomUuid();
    return deviceIdForPage;
  }
}

async function currentOwner(): Promise<{ ownerId: string; token: string } | null> {
  const sb = getSupabase();
  if (!sb) return null;
  const { data } = await sb.auth.getSession();
  const ownerId = data.session?.user?.id;
  const token = data.session?.access_token;
  if (!ownerId || !token || (await getContentOwnerBinding()) !== ownerId) return null;
  return { ownerId, token };
}

async function registryRequest(
  action: RegistryAction,
  body: Record<string, unknown>,
  continuation?: CaptureContinuation,
): Promise<RegistryResponse> {
  if (!enhancedVoiceAllowed()) throw new Error('audio-registry-disabled');
  const timing = action === 'register' || action === 'uploaded' ? createVoiceTiming(action) : undefined;
  try {
    timing?.start('auth');
    const owner = await currentOwner();
    timing?.end('auth');
    if (!owner) throw new Error('audio-registry-owner-unavailable');
    // Asked again after the sign-in and binding reads, immediately before
    // sending: a capture retired during them sends nothing.
    assertContinuation(continuation);
    timing?.start('request');
    const response = await fetch(REGISTRY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${owner.token}`,
      },
      body: JSON.stringify({ requestVersion: 1, action, ...body }),
    });
    timing?.end('request');
    timing?.server(response);
    timing?.start('response_body');
    const data = (await response.json().catch(() => null)) as RegistryResponse | null;
    timing?.end('response_body');
    if (!response.ok || !data?.ok) {
      throw new AudioRegistryProtocolError(
        data?.code ?? String(response.status),
        data?.current,
      );
    }
    return data;
  } finally {
    timing?.finish();
  }
}

export async function sha256Blob(blob: Blob): Promise<string> {
  const bytes = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)]
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('');
}

function destinationKind(row: db.AudioQueueRow): RemoteRegistryRow['destinationKind'] {
  switch (row.recoveryKind) {
    case 'new-prayer':
    case 'progress-update':
    case 'testimony':
    case 'bookmark':
      return row.recoveryKind;
    default:
      throw new Error('audio-registry-destination-unknown');
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function normalizeRemoteRecord(value: unknown): RemoteRegistryRow | null {
  if (!isRecord(value)) return null;
  const destinationKindValue = value.destinationKind;
  const captureMode = value.captureMode;
  const processingState = value.processingState;
  const registryState = value.registryState;
  const destinationState = value.destinationState;
  const entityType = value.destinationEntityType;
  const entityId = value.destinationEntityId;
  const subId = value.destinationSubId;
  const opId = value.destinationOpId;
  const leaseToken = value.recoveryLeaseToken;
  const leaseExpiry = value.recoveryLeaseExpiresAt;
  if (
    typeof value.queueId !== 'string' || !UUID_V4.test(value.queueId) ||
    (captureMode !== 'enhanced' && captureMode !== 'browser') ||
    (destinationKindValue !== 'new-prayer' &&
      destinationKindValue !== 'progress-update' &&
      destinationKindValue !== 'testimony' &&
      destinationKindValue !== 'bookmark') ||
    typeof value.destinationId !== 'string' || !UUID_V4.test(value.destinationId) ||
    typeof value.sourceDeviceId !== 'string' || !UUID_V4.test(value.sourceDeviceId) ||
    typeof value.byteSize !== 'number' || !Number.isSafeInteger(value.byteSize) ||
    typeof value.mimeType !== 'string' || !value.mimeType ||
    typeof value.language !== 'string' || !['en', 'es', 'pt'].includes(value.language) ||
    typeof value.capturedAt !== 'string' || !Number.isFinite(Date.parse(value.capturedAt)) ||
    typeof value.attemptCount !== 'number' || !Number.isSafeInteger(value.attemptCount) ||
    value.attemptCount < 0 ||
    typeof processingState !== 'string' || !REGISTRY_STATES.has(processingState) ||
    typeof registryState !== 'string' || registryState !== processingState ||
    typeof destinationState !== 'string' || !DESTINATION_STATES.has(destinationState) ||
    typeof value.registryRevision !== 'string' ||
    !REGISTRY_REVISION.test(value.registryRevision) ||
    (entityType !== null && entityType !== 'prayer' && entityType !== 'bookmark') ||
    (entityId !== null && typeof entityId !== 'string') ||
    (subId !== null && typeof subId !== 'string') ||
    (opId !== null && (typeof opId !== 'string' || !UUID_V4.test(opId))) ||
    ((entityType === null || entityId === null || opId === null) &&
      (entityType !== null || entityId !== null || opId !== null)) ||
    (subId !== null && entityType === null) ||
    (leaseToken !== null && (typeof leaseToken !== 'string' || !UUID_V4.test(leaseToken))) ||
    (leaseExpiry !== null &&
      (typeof leaseExpiry !== 'string' || !Number.isFinite(Date.parse(leaseExpiry)))) ||
    (leaseToken === null) !== (leaseExpiry === null) ||
    (value.transcript !== null && typeof value.transcript !== 'string')
  ) return null;
  if (captureMode === 'enhanced') {
    if (
      typeof value.storagePath !== 'string' || !value.storagePath ||
      value.byteSize <= 0 ||
      typeof value.byteSha256 !== 'string' || !SHA256.test(value.byteSha256)
    ) return null;
  } else if (
    value.storagePath !== null || value.byteSize !== 0 || value.byteSha256 !== null ||
    value.mimeType !== 'text/plain' || typeof value.transcript !== 'string' ||
    !value.transcript.trim()
  ) return null;
  if (value.scriptureAnchor !== null && !isRecord(value.scriptureAnchor)) return null;
  return value as unknown as RemoteRegistryRow;
}

function remoteDisposition(
  destinationState: string,
  current?: db.AudioQueueRow,
): db.AudioQueueRow['localDisposition'] {
  if (destinationState === 'bound' || destinationState === 'destination_committed') {
    return 'destination-commit-pending';
  }
  if (destinationState === 'discarded' || destinationState === 'account_erasure') {
    return 'discard-pending';
  }
  if (
    current?.localDisposition === 'destination-commit-pending' ||
    current?.localDisposition === 'discard-pending'
  ) return current.localDisposition;
  return 'unresolved';
}

function publishRegistryRow(row: db.AudioQueueRow, listFence: QueueFence): void {
  useAudioQueueStore.getState().updateItem(listFence, row.id, {
    sourceDeviceId: row.sourceDeviceId,
    prayerId: row.prayerId,
    storagePath: row.storagePath,
    scriptureAnchor: row.scriptureAnchor,
    transcribedText: row.transcribedText,
    attemptCount: row.attemptCount,
    failed: row.failed,
    awaitingInlineSave: row.awaitingInlineSave,
    byteSha256: row.byteSha256,
    localDisposition: row.localDisposition,
    registryRevision: row.registryRevision,
    registryState: row.registryState,
    destinationState: row.destinationState,
    destinationEntityType: row.destinationEntityType,
    destinationEntityId: row.destinationEntityId,
    destinationSubId: row.destinationSubId,
    destinationOpId: row.destinationOpId,
    recoveryLeaseToken: row.recoveryLeaseToken,
    recoveryLeaseExpiresAt: row.recoveryLeaseExpiresAt,
  });
}

function sameJson(left: unknown, right: unknown): boolean {
  return JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
}

function remoteConflictsWithLocal(
  current: db.AudioQueueRow,
  remote: db.AudioQueueRow,
): boolean {
  return current.ownerId !== remote.ownerId ||
    current.audioProtocolVersion !== 1 ||
    current.captureMode !== remote.captureMode ||
    current.destinationId !== remote.destinationId ||
    current.recoveryKind !== remote.recoveryKind ||
    (current.storagePath !== undefined && current.storagePath !== remote.storagePath) ||
    (current.byteSha256 !== undefined && current.byteSha256 !== remote.byteSha256) ||
    (current.sizeBytes !== undefined && current.sizeBytes !== remote.sizeBytes) ||
    current.mimeType !== remote.mimeType ||
    current.language !== remote.language ||
    new Date(current.capturedAt).getTime() !== new Date(remote.capturedAt).getTime() ||
    !sameJson(current.scriptureAnchor, remote.scriptureAnchor) ||
    Boolean(
      current.transcribedText?.trim() && remote.transcribedText?.trim() &&
      current.transcribedText.trim() !== remote.transcribedText.trim(),
    ) ||
    Boolean(
      current.destinationEntityType && remote.destinationEntityType &&
      current.destinationEntityType !== remote.destinationEntityType,
    ) ||
    Boolean(
      current.destinationEntityId && remote.destinationEntityId &&
      current.destinationEntityId !== remote.destinationEntityId,
    ) ||
    Boolean(
      current.destinationSubId && remote.destinationSubId &&
      current.destinationSubId !== remote.destinationSubId,
    ) ||
    Boolean(
      current.destinationOpId && remote.destinationOpId &&
      current.destinationOpId !== remote.destinationOpId,
    );
}

function sameLocalAuthorityTuple(
  current: db.AudioQueueRow,
  expected: db.AudioQueueRow,
): boolean {
  return current.ownerId === expected.ownerId &&
    current.registryRevision === expected.registryRevision &&
    current.localDisposition === expected.localDisposition &&
    current.destinationState === expected.destinationState &&
    (current.destinationOpId ?? null) === (expected.destinationOpId ?? null) &&
    (current.destinationEntityType ?? null) === (expected.destinationEntityType ?? null) &&
    (current.destinationEntityId ?? null) === (expected.destinationEntityId ?? null) &&
    (current.destinationSubId ?? null) === (expected.destinationSubId ?? null) &&
    (current.recoveryLeaseToken ?? null) === (expected.recoveryLeaseToken ?? null);
}

function remoteRowToLocal(ownerId: string, wire: RemoteRegistryRow): db.AudioQueueRow | null {
  const scriptureAnchor = wire.scriptureAnchor === null
    ? undefined
    : parseScriptureAnchor(wire.scriptureAnchor) ?? undefined;
  if (
    wire.captureMode !== 'enhanced' ||
    typeof wire.storagePath !== 'string' ||
    (wire.destinationKind === 'bookmark' && !scriptureAnchor)
  ) return null;
  const destinationIsPending =
    wire.destinationState === 'bound' || wire.destinationState === 'destination_committed';
  return {
    id: wire.queueId,
    ownerId,
    audioProtocolVersion: 1,
    captureMode: wire.captureMode,
    destinationId: wire.destinationId,
    sourceDeviceId: wire.sourceDeviceId,
    prayerId:
      wire.destinationKind === 'progress-update' || wire.destinationKind === 'testimony'
        ? wire.destinationId
        : wire.destinationEntityType === 'prayer' && wire.destinationEntityId
          ? wire.destinationEntityId
          : null,
    storagePath: wire.storagePath,
    sizeBytes: wire.byteSize,
    byteSha256: wire.byteSha256 ?? undefined,
    mimeType: wire.mimeType,
    capturedAt: wire.capturedAt,
    language: wire.language,
    transcribedText: wire.transcript?.trim() || null,
    attemptCount: wire.attemptCount,
    failed: wire.processingState === 'failed' || wire.processingState === 'parked',
    awaitingInlineSave:
      !destinationIsPending && wire.destinationState === 'unresolved' &&
      (wire.destinationKind === 'new-prayer' || wire.destinationKind === 'bookmark'),
    inlineSaveSource: wire.destinationKind === 'bookmark' ? 'bookmark' : 'prayer',
    scriptureAnchor,
    recoveryKind: wire.destinationKind,
    localDisposition: remoteDisposition(wire.destinationState),
    registryRevision: wire.registryRevision,
    registryState: wire.registryState,
    destinationState: wire.destinationState,
    destinationEntityType: wire.destinationEntityType ?? undefined,
    destinationEntityId: wire.destinationEntityId ?? undefined,
    destinationSubId: wire.destinationSubId ?? undefined,
    destinationOpId: wire.destinationOpId ?? undefined,
    recoveryLeaseToken: wire.recoveryLeaseToken ?? undefined,
    recoveryLeaseExpiresAt: wire.recoveryLeaseExpiresAt ?? undefined,
  };
}

/**
 * A live capture's continuation check, passed in by the audio queue (voice-
 * capture custody, account-change recording plan rev 4.1, section 3.4). When
 * it answers false the response is not applied: no row write, no list update,
 * no further request. The error text matches audio-queue.ts
 * `CAPTURE_RETIRED_ERROR` (kept local to avoid an import cycle).
 */
export type CaptureContinuation = () => boolean;
const CAPTURE_RETIRED = 'audio-capture-retired';

function assertContinuation(continuation?: CaptureContinuation): void {
  if (continuation && !continuation()) throw new Error(CAPTURE_RETIRED);
}

// ---- Recordings made before v0.2.0: proofs kept for this page session ----
// db.ts ("Recordings made before v0.2.0") mints a verified copy of such a
// recording only against a proof kept here: the registry record's {owner, queue
// id, storage path, byte size, SHA-256, MIME type}, kept when a merge caller
// established that the record existed before its operation read the bytes (a
// discovery answer, or an answer for a row already registered when the
// operation began). Memory only, never persisted; every proof is dropped once
// the signed-in owner or the auth epoch differs from when it was kept.
interface KeptLegacyRecordingProof {
  readonly proof: db.LegacyRecordingProof;
  readonly authUserId: string;
  readonly authEpoch: number;
}
const keptLegacyRecordingProofs = new Map<string, KeptLegacyRecordingProof>();
const legacyProofKey = (ownerId: string, queueId: string) => `${ownerId}:${queueId}`;

/** Drops every kept proof whose owner or auth epoch is no longer the current one. */
function dropLegacyProofsAfterAuthChange(): void {
  const userId = getAuthUserId();
  const epoch = getAuthEpoch();
  for (const [key, kept] of keptLegacyRecordingProofs) {
    if (kept.authUserId !== userId || kept.authEpoch !== epoch) keptLegacyRecordingProofs.delete(key);
  }
}

function keptLegacyRecordingProof(ownerId: string, queueId: string): db.LegacyRecordingProof | null {
  dropLegacyProofsAfterAuthChange();
  const kept = keptLegacyRecordingProofs.get(legacyProofKey(ownerId, queueId));
  return kept && kept.proof.ownerId === ownerId && kept.proof.queueId === queueId ? kept.proof : null;
}
db.setLegacyRecordingProofLookup(keptLegacyRecordingProof);

/**
 * Keeps `wire` as the proof for a recording made before v0.2.0 (legacy
 * compatibility contract, sections 3 and 4). The caller has established that
 * the record existed before its operation read the bytes. Kept only for the
 * signed-in owner, an enhanced record that is not quarantined, and a local row
 * that still holds its Blob, binds to the record and does not conflict with it.
 * Reads the row, never its bytes; keeps nothing on any doubt.
 */
async function keepLegacyRecordingProof(
  ownerId: string,
  wire: RemoteRegistryRow,
  remote: db.AudioQueueRow,
): Promise<void> {
  const authUserId = getAuthUserId();
  const authEpoch = getAuthEpoch();
  if (authUserId !== ownerId || wire.captureMode !== 'enhanced' || wire.processingState === 'quarantined') return;
  if (
    typeof wire.storagePath !== 'string' || !wire.storagePath ||
    typeof wire.byteSha256 !== 'string' || !SHA256.test(wire.byteSha256) ||
    !Number.isSafeInteger(wire.byteSize) || wire.byteSize <= 0
  ) return;
  const proof: db.LegacyRecordingProof = Object.freeze({
    ownerId,
    queueId: wire.queueId,
    storagePath: wire.storagePath,
    byteSize: wire.byteSize,
    byteSha256: wire.byteSha256,
    mimeType: wire.mimeType,
  });
  let current: db.AudioQueueRow | undefined;
  try {
    current = await db.getAudioQueueRow(wire.queueId);
  } catch {
    return;
  }
  if (!current || !db.legacyRecordingProofApplies(current, proof) || remoteConflictsWithLocal(current, remote)) return;
  if (getAuthUserId() !== authUserId || getAuthEpoch() !== authEpoch) return;
  keptLegacyRecordingProofs.set(legacyProofKey(ownerId, wire.queueId), Object.freeze({ proof, authUserId, authEpoch }));
}

/**
 * `legacyProofEligible`: the caller established that this record existed
 * before its operation read the recording's bytes (a discovery answer, or an
 * answer for a row already registered when the operation began), so it may be
 * kept as the proof for a recording made before v0.2.0. Never for the first
 * registration that computed the digest.
 */
async function mergeRegistryRecord(
  listFence: QueueFence,
  expectedOwnerId: string,
  value: unknown,
  expectedQueueId?: string,
  expectedLocalAuthority?: db.AudioQueueRow,
  continuation?: CaptureContinuation,
  verifiedCopy?: db.VerifiedCaptureCopy | null,
  legacyProofEligible = false,
  // LC4: the launch discovery does not rewrite a recording whose bytes do not
  // match their reference (db.ts `RecordingWriteOptions`).
  refuseMismatch = false,
): Promise<db.AudioQueueRow> {
  assertContinuation(continuation);
  const wire = normalizeRemoteRecord(value);
  if (!wire || (expectedQueueId && wire.queueId !== expectedQueueId)) {
    throw new Error('audio-registry-response-invalid');
  }
  // A record shown quarantined is no proof (LC5): any proof kept for it in this
  // page session is dropped before the write below can use it.
  if (wire.processingState === 'quarantined') {
    keptLegacyRecordingProofs.delete(legacyProofKey(expectedOwnerId, wire.queueId));
  }
  const remote = remoteRowToLocal(expectedOwnerId, wire);
  if (!remote) throw new Error('audio-registry-response-unsupported');
  // Before the write below, so its copy (proved before its transaction) can
  // use the proof.
  if (legacyProofEligible) await keepLegacyRecordingProof(expectedOwnerId, wire, remote);
  let conflicted = false;
  let responseStale = false;
  let refused = false;
  const patched = await db.updateAudioQueueRowIfPresent(
    remote.id,
    expectedOwnerId,
    (current) => {
      if (continuation && !continuation()) {
        refused = true;
        return null;
      }
      if (
        expectedLocalAuthority &&
        !sameLocalAuthorityTuple(current, expectedLocalAuthority)
      ) {
        responseStale = true;
        return null;
      }
      // Each established conflict records why the row is quarantined,
      // replacing any earlier reason (plan rev 4.1, section 4.5), so a later
      // recovery step can tell a registry conflict from a kept recording.
      if (remoteConflictsWithLocal(current, remote)) {
        conflicted = true;
        return { ...current, localDisposition: 'quarantined', quarantineReason: 'registry-conflict' };
      }
      if (
        current.registryRevision &&
        !REGISTRY_REVISION.test(current.registryRevision)
      ) {
        conflicted = true;
        return { ...current, localDisposition: 'quarantined', quarantineReason: 'registry-revision-malformed' };
      }
      if (
        current.registryRevision &&
        compareRevision(wire.registryRevision, current.registryRevision) < 0
      ) return current;
      if (
        current.registryRevision === wire.registryRevision && (
          (current.registryState !== undefined && current.registryState !== wire.registryState) ||
          (current.destinationState !== undefined &&
            current.destinationState !== wire.destinationState) ||
          (current.recoveryLeaseToken !== undefined &&
            current.recoveryLeaseToken !== (wire.recoveryLeaseToken ?? undefined)) ||
          (current.recoveryLeaseExpiresAt !== undefined &&
            current.recoveryLeaseExpiresAt !== (wire.recoveryLeaseExpiresAt ?? undefined))
        )
      ) {
        conflicted = true;
        return { ...current, localDisposition: 'quarantined', quarantineReason: 'registry-state-conflict' };
      }
      const hasLocalDestination = Boolean(
        current.destinationEntityType && current.destinationEntityId && current.destinationOpId,
      );
      const destinationState = wire.destinationState;
      return {
        ...current,
        sourceDeviceId: wire.sourceDeviceId,
        prayerId: remote.prayerId ?? current.prayerId,
        storagePath: remote.storagePath,
        sizeBytes: remote.sizeBytes,
        byteSha256: remote.byteSha256,
        scriptureAnchor: remote.scriptureAnchor,
        transcribedText: remote.transcribedText ?? current.transcribedText,
        attemptCount: Math.max(current.attemptCount, remote.attemptCount),
        failed: remote.failed,
        awaitingInlineSave:
          destinationState === 'unresolved' && !hasLocalDestination && current.prayerId === null
            ? remote.awaitingInlineSave
            : false,
        localDisposition: remoteDisposition(destinationState, current),
        registryRevision: wire.registryRevision,
        registryState: wire.registryState,
        destinationState,
        destinationEntityType:
          remote.destinationEntityType ?? current.destinationEntityType,
        destinationEntityId: remote.destinationEntityId ?? current.destinationEntityId,
        destinationSubId: remote.destinationSubId ?? current.destinationSubId,
        destinationOpId: remote.destinationOpId ?? current.destinationOpId,
        recoveryLeaseToken: remote.recoveryLeaseToken,
        recoveryLeaseExpiresAt: remote.recoveryLeaseExpiresAt,
      };
    },
    verifiedCopy,
    refuseMismatch ? { refuseMismatch: true } : undefined,
  );
  if (refused) throw new Error(CAPTURE_RETIRED);
  if (!patched) {
    throw new Error(
      responseStale
        ? 'audio-registry-local-authority-changed'
        : 'audio-registry-local-row-missing',
    );
  }
  // Refused after the write returned: the write already queued stands, but
  // nothing is published and the caller takes no further step.
  assertContinuation(continuation);
  publishRegistryRow(patched, listFence);
  if (conflicted) {
    // A record the row conflicts with is no proof for it.
    keptLegacyRecordingProofs.delete(legacyProofKey(expectedOwnerId, wire.queueId));
    throw new Error('audio-registry-local-provenance-conflict');
  }
  return patched;
}

async function mergeProtocolCurrent(
  row: db.AudioQueueRow,
  error: AudioRegistryProtocolError,
  listFence: QueueFence,
  continuation?: CaptureContinuation,
  verifiedCopy?: db.VerifiedCaptureCopy | null,
): Promise<db.AudioQueueRow | null> {
  if (!error.current || !row.ownerId) return null;
  return mergeRegistryRecord(listFence, row.ownerId, error.current, row.id, row, continuation, verifiedCopy);
}

/**
 * The passage a registration sends (mic repair F1, 2026-10-20): the content-
 * document form of contracts.ts normalizeAnchor, exactly eight keys with an
 * absent `wholeChapter` as null. register_audio_capture_v1 runs
 * content_validate_anchor_strict_v1, which refuses any other shape (the real
 * server answered every journal and Pray on Scripture verse recording 400), and
 * reconcile compares the registered passage with the saved document's as jsonb.
 * Only the request changes: the queue row keeps the passage it stores, and a
 * registry answer is still parsed back into that form (remoteRowToLocal), so
 * the merge compares like with like. A whole-chapter passage already had all
 * eight keys and is sent byte for byte as before. A stored value that is not a
 * valid passage is sent as it is, for the server to refuse as before.
 */
function registryPassage(stored: unknown): unknown {
  if (stored === undefined || stored === null) return null;
  const passage = parseScriptureAnchor(stored);
  return passage ? normalizeAnchor(passage) : stored;
}

/**
 * Register only after the Blob is durable in IndexedDB. The server derives the
 * canonical path and returns an exact-path, non-upsert signed upload token, so
 * cached clients retain no broad Storage mutation authority. `verifiedCopy`
 * (KR8, db.ts "Verified capture copies"): the caller's proved copy of this
 * row's bytes; its digest is the registered one, and both rewrites here store
 * it in place of the Blob they read.
 */
export async function registerDurableAudio(
  row: db.AudioQueueRow,
  listFence: QueueFence = captureQueueListFence(),
  continuation?: CaptureContinuation,
  verifiedCopy?: db.VerifiedCaptureCopy | null,
  // LC2 decision 2: the SHA-256 of the bytes the caller read from this row in
  // this operation, used when the row has no digest yet (the bytes it uploads
  // are those bytes). Not a proof and never kept as one.
  bytesRead?: { rowId: string; sha256: string },
): Promise<RegistryResponse> {
  if (
    row.audioProtocolVersion !== 1 ||
    row.captureMode !== 'enhanced' ||
    !row.ownerId ||
    !row.destinationId ||
    !row.sourceDeviceId ||
    !row.blob
  ) {
    throw new Error('audio-registry-row-not-authoritative');
  }
  // `row` is the caller's snapshot from before this operation read any bytes:
  // when it was already registered with a digest, the answer below may replay a
  // record that existed before this operation (a proof for a recording made
  // before v0.2.0); a first registration's answer never is. Its revision then
  // decides (D1, LC5; see `replaysRecordThatExisted`).
  const revisionAtStart = row.registryRevision && REGISTRY_REVISION.test(row.registryRevision) && row.byteSha256
    ? row.registryRevision
    : null;
  const digest = row.byteSha256 && SHA256.test(row.byteSha256)
    ? row.byteSha256
    : verifiedCopy && verifiedCopy.rowId === row.id
      ? verifiedCopy.sha256
      : bytesRead && bytesRead.rowId === row.id && SHA256.test(bytesRead.sha256)
        ? bytesRead.sha256
        : await sha256Blob(row.blob);
  assertContinuation(continuation);
  if (!row.byteSha256) {
    let refused = false;
    const patched = await db.updateAudioQueueRowIfPresent(
      row.id,
      row.ownerId,
      (current) => {
        if (continuation && !continuation()) {
          refused = true;
          return null;
        }
        return { ...current, byteSha256: digest };
      },
      verifiedCopy,
    );
    if (refused) throw new Error(CAPTURE_RETIRED);
    if (!patched) throw new Error('audio-registry-row-changed');
  }
  assertContinuation(continuation);
  // The capture's continuation travels with the request (it is asked again
  // after the sign-in reads, before sending); the body below is all that is sent.
  const register = (body: Record<string, unknown>) =>
    registryRequest('register', body, continuation);
  const result = await register({
    queueId: row.id,
    captureMode: row.captureMode,
    destinationKind: destinationKind(row),
    destinationId: row.destinationId,
    sourceDeviceId: row.sourceDeviceId,
    mimeType: row.mimeType,
    byteSize: row.sizeBytes ?? row.blob.size,
    byteSha256: digest,
    language: row.language,
    capturedAt: row.capturedAt,
    scriptureAnchor: registryPassage(row.scriptureAnchor),
  });
  // The canonical path is only an address. `registryState: registered` means
  // the provider object is still unproved, so callers must retain the Blob and
  // observe it through the `uploaded` action before treating the upload as done.
  const replayedRecordThatExisted = revisionAtStart !== null && replaysRecordThatExisted(result.registryRevision, revisionAtStart);
  await mergeRegistryRecord(
    listFence, row.ownerId, result, row.id, row, continuation, verifiedCopy, replayedRecordThatExisted,
  );
  return result;
}

/**
 * D1 (LC5): whether a register answer replays a record that existed before the
 * operation. The route does not pass the RPC's `replay` flag through, but
 * migration 044 answers an insert with revision 1 (the column default) and a
 * replay of a live `registered` record with its revision raised by one; any
 * other replay keeps its revision. So only an answer above the row's revision
 * when the operation began replays a record that existed; a record the server
 * inserted just now from this row's own digest and size never qualifies, nor
 * does a replay that kept its revision (no proof: the safe direction).
 */
function replaysRecordThatExisted(answered: unknown, revisionAtStart: string): boolean {
  return typeof answered === 'string' && REGISTRY_REVISION.test(answered) &&
    compareRevision(answered, revisionAtStart) > 0;
}

export async function markRegistryUploaded(
  queueId: string,
  registryRevision?: string,
  listFence: QueueFence = captureQueueListFence(),
  continuation?: CaptureContinuation,
  verifiedCopy?: db.VerifiedCaptureCopy | null,
): Promise<db.AudioQueueRow> {
  assertContinuation(continuation);
  const owner = await currentOwner();
  const initial = await db.getAudioQueueRow(queueId);
  if (!owner || !initial || initial.ownerId !== owner.ownerId) {
    throw new Error('audio-registry-upload-row-missing');
  }
  let snapshot = initial;
  if (registryRevision && snapshot.registryRevision !== registryRevision) {
    if (!snapshot.registryRevision || compareRevision(snapshot.registryRevision, registryRevision) < 0) {
      throw new Error('audio-registry-upload-row-changed');
    }
  }
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      assertContinuation(continuation);
      const result = await registryRequest('uploaded', {
        queueId,
        expectedRegistryRevision: snapshot.registryRevision ?? registryRevision ?? null,
      }, continuation);
      return mergeRegistryRecord(listFence, owner.ownerId, result, queueId, snapshot, continuation, verifiedCopy);
    } catch (error) {
      if (!(error instanceof AudioRegistryProtocolError)) throw error;
      const current = await mergeProtocolCurrent(snapshot, error, listFence, continuation, verifiedCopy);
      if (error.code !== 'revision_conflict' || !current || attempt === 1) throw error;
      snapshot = current;
    }
  }
  throw new Error('audio-registry-upload-unreachable');
}

/**
 * Claims (or refreshes) this device's recovery lease. NOT a read: with the
 * current revision it grants or rotates the lease and raises the revision; a
 * stale revision answers `revision_conflict` with the current record, which is
 * merged and tried once more. An optional capture continuation is asked before
 * each request and before each merge is published.
 */
export async function claimRegistryRecovery(
  row: db.AudioQueueRow,
  listFence: QueueFence,
  continuation?: CaptureContinuation,
): Promise<db.AudioQueueRow> {
  if (!row.ownerId || !row.registryRevision) {
    throw new Error('audio-registry-claim-authority-missing');
  }
  const ownerId = row.ownerId;
  let snapshot = row;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      assertContinuation(continuation);
      const result = await registryRequest('claim', {
        queueId: snapshot.id,
        expectedRegistryRevision: snapshot.registryRevision,
        sourceDeviceId: getAudioDeviceId(),
      }, continuation);
      return await mergeRegistryRecord(listFence, ownerId, result, snapshot.id, snapshot, continuation);
    } catch (error) {
      if (!(error instanceof AudioRegistryProtocolError)) throw error;
      const current = await mergeProtocolCurrent(snapshot, error, listFence, continuation);
      if (!current || attempt === 1) throw error;
      snapshot = current;
    }
  }
  throw new Error('audio-registry-claim-unreachable');
}

async function registryMutation(
  row: db.AudioQueueRow,
  action: Extract<RegistryAction, 'bind' | 'reconcile' | 'retry' | 'park' | 'discard'>,
  body: (snapshot: db.AudioQueueRow) => Record<string, unknown>,
  listFence: QueueFence,
  continuation?: CaptureContinuation,
): Promise<{ row: db.AudioQueueRow; result: RegistryResponse }> {
  if (!row.ownerId || !row.registryRevision) {
    throw new Error('audio-registry-mutation-authority-missing');
  }
  const ownerId = row.ownerId;
  let snapshot = row;
  let leaseRefreshUsed = false;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      assertContinuation(continuation);
      const result = await registryRequest(action, body(snapshot), continuation);
      const patched = await mergeRegistryRecord(
        listFence,
        ownerId,
        result,
        snapshot.id,
        snapshot,
        continuation,
      );
      return { row: patched, result };
    } catch (error) {
      if (!(error instanceof AudioRegistryProtocolError)) throw error;
      const current = await mergeProtocolCurrent(snapshot, error, listFence, continuation);
      if (error.code === 'revision_conflict' && current) {
        snapshot = current;
        continue;
      }
      if (error.code === 'lease_conflict' && !leaseRefreshUsed) {
        snapshot = await claimRegistryRecovery(snapshot, listFence, continuation);
        leaseRefreshUsed = true;
        continue;
      }
      throw error;
    }
  }
  throw new Error('audio-registry-mutation-retry-exhausted');
}

export async function markRegistryParked(
  queueId: string,
  registryRevision?: string,
  recoveryLeaseToken?: string,
  listFence: QueueFence = captureQueueListFence(),
): Promise<db.AudioQueueRow> {
  const owner = await currentOwner();
  const row = await db.getAudioQueueRow(queueId);
  if (
    !owner || !row || row.ownerId !== owner.ownerId ||
    (registryRevision && row.registryRevision !== registryRevision) ||
    (recoveryLeaseToken && row.recoveryLeaseToken !== recoveryLeaseToken)
  ) throw new Error('audio-registry-park-row-changed');
  return (await registryMutation(row, 'park', (snapshot) => ({
    queueId,
    expectedRegistryRevision: snapshot.registryRevision,
    recoveryLeaseToken: snapshot.recoveryLeaseToken ?? null,
  }), listFence)).row;
}

export async function retryRegistryAudio(
  queueId: string,
  registryRevision?: string,
  recoveryLeaseToken?: string,
  listFence: QueueFence = captureQueueListFence(),
  continuation?: CaptureContinuation,
): Promise<db.AudioQueueRow> {
  assertContinuation(continuation);
  const owner = await currentOwner();
  const row = await db.getAudioQueueRow(queueId);
  assertContinuation(continuation);
  if (
    !owner || !row || row.ownerId !== owner.ownerId ||
    (registryRevision && row.registryRevision !== registryRevision) ||
    (recoveryLeaseToken && row.recoveryLeaseToken !== recoveryLeaseToken)
  ) throw new Error('audio-registry-retry-row-changed');
  return (await registryMutation(row, 'retry', (snapshot) => ({
    queueId,
    expectedRegistryRevision: snapshot.registryRevision,
    recoveryLeaseToken: snapshot.recoveryLeaseToken ?? null,
  }), listFence, continuation)).row;
}

function deletionAuthority(
  row: db.AudioQueueRow,
  result: RegistryResponse,
  terminalState: 'destination_committed' | 'discarded',
): db.AudioQueueDeletionAuthority | null {
  if (
    result.deleted !== true ||
    typeof result.receiptId !== 'string' ||
    !UUID_V4.test(result.receiptId) ||
    result.queueId !== row.id ||
    typeof result.registryRevision !== 'string' ||
    result.registryRevision !== row.registryRevision ||
    result.destinationState !== terminalState ||
    (result.destinationOpId ?? null) !== (row.destinationOpId ?? null) ||
    (result.destinationEntityType ?? null) !== (row.destinationEntityType ?? null) ||
    (result.destinationEntityId ?? null) !== (row.destinationEntityId ?? null) ||
    (result.destinationSubId ?? null) !== (row.destinationSubId ?? null) ||
    !row.registryRevision
  ) return null;
  return {
    kind: 'registry-deletion-receipt',
    receiptId: result.receiptId,
    expectedRegistryRevision: row.registryRevision,
    expectedDestinationOpId: row.destinationOpId ?? null,
    expectedDestinationEntityType: row.destinationEntityType ?? null,
    expectedDestinationEntityId: row.destinationEntityId ?? null,
    expectedDestinationSubId: row.destinationSubId ?? null,
  };
}

export async function bindRegistryToContentReceipt(
  row: db.AudioQueueRow,
  listFence: QueueFence = captureQueueListFence(),
): Promise<db.AudioQueueRow | null> {
  if (
    row.captureMode !== 'enhanced' ||
    !row.destinationOpId ||
    !row.destinationEntityType ||
    !row.destinationEntityId
  ) return null;
  const mutation = await registryMutation(row, 'bind', (snapshot) => ({
    queueId: snapshot.id,
    expectedRegistryRevision: snapshot.registryRevision,
    destinationEntityType: snapshot.destinationEntityType,
    destinationEntityId: snapshot.destinationEntityId,
    destinationSubId: snapshot.destinationSubId ?? null,
    destinationOpId: snapshot.destinationOpId,
    recoveryLeaseToken: snapshot.recoveryLeaseToken ?? null,
  }), listFence);
  const { result } = mutation;
  if (
    result.queueId !== row.id ||
    typeof result.registryRevision !== 'string' ||
    result.destinationState !== 'bound' ||
    (result.destinationOpId ?? null) !== row.destinationOpId ||
    (result.destinationEntityType ?? null) !== row.destinationEntityType ||
    (result.destinationEntityId ?? null) !== row.destinationEntityId ||
    (result.destinationSubId ?? null) !== (row.destinationSubId ?? null)
  ) throw new Error('audio-registry-bind-response-mismatch');
  return mutation.row;
}

/** Server verifies the exact content receipt and performs normal exact cleanup. */
export async function reconcileRegistryAudio(
  row: db.AudioQueueRow,
  listFence: QueueFence = captureQueueListFence(),
): Promise<{
  row: db.AudioQueueRow;
  authority: db.AudioQueueDeletionAuthority | null;
} | null> {
  if (row.captureMode !== 'enhanced') return null;
  const mutation = await registryMutation(row, 'reconcile', (snapshot) => ({
    queueId: snapshot.id,
    expectedRegistryRevision: snapshot.registryRevision,
    destinationOpId: snapshot.destinationOpId ?? null,
    recoveryLeaseToken: snapshot.recoveryLeaseToken ?? null,
  }), listFence);
  return {
    row: mutation.row,
    authority: deletionAuthority(
      mutation.row,
      mutation.result,
      'destination_committed',
    ),
  };
}

/** Explicit discard is a server terminal transition, never a client DELETE. */
export async function discardRegistryAudio(
  row: db.AudioQueueRow,
  listFence: QueueFence = captureQueueListFence(),
): Promise<db.AudioQueueDeletionAuthority | null> {
  if (row.captureMode !== 'enhanced') return null;
  let snapshot = row;
  let revision = snapshot.registryRevision;
  if (!revision) {
    if (!snapshot.blob) return null;
    const registered = await registerDurableAudio(snapshot, listFence);
    revision = registered.registryRevision;
    const refreshed = await db.getAudioQueueRow(snapshot.id);
    if (!refreshed || refreshed.ownerId !== snapshot.ownerId) return null;
    snapshot = refreshed;
  }
  const mutation = await registryMutation(snapshot, 'discard', (current) => ({
    queueId: current.id,
    expectedRegistryRevision: current.registryRevision ?? revision ?? null,
    recoveryLeaseToken: current.recoveryLeaseToken ?? null,
  }), listFence);
  return deletionAuthority(mutation.row, mutation.result, 'discarded');
}

/** Discover/claim unresolved server-backed audio for this exact owner/device. */
export async function discoverRegistryAudio(
  listFence: QueueFence = captureQueueListFence(),
): Promise<db.AudioQueueRow[]> {
  const owner = await currentOwner();
  if (!owner || !enhancedVoiceAllowed()) return [];
  const result = await registryRequest('discover', {
    sourceDeviceId: getAudioDeviceId(),
    limit: 50,
  });
  const remote = Array.isArray(result.rows) ? result.rows : [];
  const installed: db.AudioQueueRow[] = [];
  for (const candidate of remote) {
    const wire = normalizeRemoteRecord(candidate);
    if (!wire) continue;
    const row = remoteRowToLocal(owner.ownerId, wire);
    if (!row) continue;
    const existing = await db.getAudioQueueRow(row.id);
    if (existing) {
      // The merge validates immutable provenance and compares decimal bigint
      // revisions before applying anything. A delayed discovery response can
      // therefore never roll back a newer bind/transcribe/reconcile result.
      // A discovery answer existed before any byte read in this launch: it may
      // be kept as the proof for a recording made before v0.2.0.
      const patched = await mergeRegistryRecord(
        listFence, owner.ownerId, wire, row.id, undefined, undefined, undefined, true, true,
      ).catch((error: unknown) => {
        // LC4: a recording this merge refused for its own bytes (unreadable in
        // this page, or not matching its reference) is left as it is; its
        // prayer says why instead of showing it as finishing. The list update
        // applies only under this hydrate's own scope and the current auth.
        if (error instanceof db.CaptureBytesUnverifiedError) {
          const attention = error.reason === 'read-failed'
            ? 'unreadable'
            : db.isRecordingBytesMismatch(error.reason) ? 'mismatch' : null;
          if (attention) useAudioQueueStore.getState().setAttention(listFence, row.id, attention);
        }
        return null;
      });
      if (patched) installed.push(patched);
      continue;
    }
    await db.saveAudioQueueRow(row, owner.ownerId);
    useAudioQueueStore.getState().upsertItem(listFence, {
      id: row.id,
      ownerId: row.ownerId,
      audioProtocolVersion: row.audioProtocolVersion,
      captureMode: row.captureMode,
      destinationId: row.destinationId,
      sourceDeviceId: row.sourceDeviceId,
      prayerId: row.prayerId,
      mimeType: row.mimeType,
      capturedAt: row.capturedAt,
      language: row.language,
      transcribedText: row.transcribedText,
      attemptCount: row.attemptCount,
      failed: row.failed,
      sizeBytes: row.sizeBytes ?? 0,
      storagePath: row.storagePath,
      awaitingInlineSave: row.awaitingInlineSave,
      inlineSaveSource: row.inlineSaveSource,
      recoveryKind: row.recoveryKind,
      byteSha256: row.byteSha256,
      localDisposition: row.localDisposition,
      registryRevision: row.registryRevision,
      registryState: row.registryState,
      destinationState: row.destinationState,
      destinationEntityType: row.destinationEntityType,
      destinationEntityId: row.destinationEntityId,
      destinationSubId: row.destinationSubId,
      destinationOpId: row.destinationOpId,
      recoveryLeaseToken: row.recoveryLeaseToken,
      recoveryLeaseExpiresAt: row.recoveryLeaseExpiresAt,
    });
    installed.push(row);
  }
  return installed;
}
